import React from 'react';
import numeral from 'numeral';
import {FeeMode, FEE_MODE} from '@/types/gigs';
import translate from '@/utils/translate';
import BidIcon from '@/components/icons/Bid';

/**
 * Format a number with abbreviation, showing decimals only when not round
 * @param value The number to format
 * @returns Formatted string (e.g., "10K" for 10000, "10.5K" for 10500)
 */
export const formatSmartAbbreviation = (value: number): string => {
  if (value < 1000) return value.toString();

  // First format with decimal to see what we get
  const withDecimal = numeral(value).format('0.0a');

  // If the decimal version ends with .0, reformat it without decimal
  if (
    withDecimal.endsWith('.0k') ||
    withDecimal.endsWith('.0m') ||
    withDecimal.endsWith('.0b') ||
    withDecimal.endsWith('.0t')
  ) {
    return numeral(value).format('0a').toUpperCase();
  }

  return withDecimal.toUpperCase();
};

/**
 * Format follower count with a plus sign and K suffix
 * @param followers The number of followers
 * @returns Formatted string (e.g., "+10K")
 */
export const formatFollowers = (followers: number | undefined): string => {
  if (followers === 0) {
    return translate('Not required');
  }
  if (!followers) {
    return '0';
  }

  const formatted = formatSmartAbbreviation(followers);
  return `+${formatted}`;
};

/**
 * Format a bid value following the same logic as the Ember implementation
 * @param value The number to format
 * @returns Formatted string
 */
export const formatBid = (value: number): string => {
  return formatSmartAbbreviation(value);
};

/**
 * Format fee display based on fee type and values
 * @param gig The gig object containing fee information
 * @returns Formatted fee display (string or icon)
 */
export const formatFeeDisplay = (
  feeType: FeeMode,
  feeAmount: number | null,
  feeSuggestedMin: number | null,
  feeSuggestedMax: number | null
): React.ReactNode => {
  if (feeType === FEE_MODE.FIXED && feeAmount !== null) {
    return `$${formatBid(feeAmount)}`;
  }

  if (feeType === FEE_MODE.SUGGESTED) {
    // If we have both min and max values
    if (feeSuggestedMin !== null && feeSuggestedMax !== null) {
      return `$${formatBid(feeSuggestedMin)} - $${formatBid(feeSuggestedMax)}`;
    }

    // If we only have min value
    if (feeSuggestedMin !== null && feeSuggestedMax === null) {
      return `$${formatBid(feeSuggestedMin)} ${translate('or more')}`;
    }

    // If we only have max value (with "Up to" prefix)
    if (feeSuggestedMin === null && feeSuggestedMax !== null) {
      return `${translate('Up to')} $${formatBid(feeSuggestedMax)}`;
    }

    // If we only have a single suggested amount
    if (feeAmount !== null) {
      return `$${formatBid(feeAmount)}`;
    }
  }

  // For 'bid' type, return a bid icon using the diamond icon to represent value/premium
  return <BidIcon width={17} height={17} />;
};

/**
 * Get fee note text based on fee type
 * @param feeType The type of fee
 * @param hasCommission Optional flag indicating if there's a commission
 * @returns Descriptive text for the fee type
 */
export const getFeeNoteText = (
  feeType: FeeMode,
  hasCommission?: boolean
): string => {
  let base: string;
  switch (feeType) {
    case FEE_MODE.FIXED:
      base = translate('Fixed Fee');
      break;
    case FEE_MODE.SUGGESTED:
      base = translate('Suggested Fee');
      break;
    case FEE_MODE.BID:
    default:
      base = translate('Bid');
      break;
  }

  return hasCommission ? `${base} + ${translate('Commission')}` : base;
};

/**
 * Format deliverables count
 * @param count The number of deliverables
 * @returns Formatted string
 */
export const formatDeliverables = (count: number | undefined): string => {
  return count?.toString() || '0';
};
